/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable arrow-body-style */
import React from "react";
import { useDispatch } from "react-redux";
import { updateUserLanguage } from "@App/appReducer";
import { buildLanguageUrl, customDomainSwitch, saveLanguagePreference } from "@Utils/languageCore";
import wrapperHOC from "@Utils/wrapperHOC";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS, APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { logAppEvent } from "@Utils/appAnalytics";

import { languagesForAmberUserWebsite } from "locales/langConfig/langConfig";
import styles from "./LanguagePreferenceModal.module.scss";
import useIsApp from "hooks/useIsApp";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  suggestedLanguage: string;
  currentPath: string;
}

/**
 * Modal that prompts users to switch to their preferred language
 * Shows when user has a saved language preference but is on a non-prefixed URL
 *
 * When user confirms, performs a full page refresh to the language-prefixed URL
 */
const LanguagePreferenceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  suggestedLanguage,
  currentPath,
}) => {
  const dispatch = useDispatch();
  const isApp = useIsApp();

  // Don't render modal for Chinese (zh) as it uses a different domain
  if (!isOpen || suggestedLanguage === "zh") return null;

  const languageConfig = languagesForAmberUserWebsite.find(
    (lang) => lang.code === suggestedLanguage,
  );
  const languageName = languageConfig?.nativeLabel || suggestedLanguage;

  const handleSwitchLanguage = () => {
    logAppEvent({
      eventName: APP_EVENTS.CTA_CLICKED,
      payload: {
        cta_name: "language_preference_modal_accept",
        modal_name: "language_preference_modal",
        from_language: "en",
        to_language: suggestedLanguage,
        to_language_name: languageName,
        current_path: currentPath,
      },
    });

    onClose();
    saveLanguagePreference(suggestedLanguage);
    dispatch(updateUserLanguage(suggestedLanguage));

    if (isApp) {
      sendWebViewEvent(WEB_VIEW_APP_EVENTS.LANGUAGE_CHANGED, {
        language: suggestedLanguage,
      });

      setTimeout(() => {
        navigateToLanguage();
      }, 500);
    } else {
      setTimeout(() => {
        navigateToLanguage();
      }, 100);
    }
  };

  const navigateToLanguage = () => {
    const urlResult = buildLanguageUrl({
      path: currentPath,
      targetLanguage: suggestedLanguage,
      currentLanguage: "en",
      preserveSearch: true,
    });

    const { search, hash, origin } = window.location;
    let fullUrl: string;

    if (urlResult.needsCrossDomainNavigation && urlResult.targetDomain) {
      // Cross-domain navigation (e.g., switching to Chinese)
      fullUrl = `${urlResult.targetDomain}${urlResult.url.replace(
        urlResult.targetDomain,
        "",
      )}${search}${hash}`;
      customDomainSwitch({ url: fullUrl });
    } else {
      // Same-domain navigation with full page refresh
      fullUrl = `${origin}${urlResult.url}${search}${hash}`;
      // In app: Use replace() to avoid creating history entry (prevents back button regression)
      // On web: Use href to allow normal browser history
      if (isApp) {
        window.location.replace(fullUrl);
      } else {
        window.location.href = fullUrl;
      }
    }
  };

  const handleStayInEnglish = () => {
    logAppEvent({
      eventName: APP_EVENTS.CTA_CLICKED,
      payload: {
        cta_name: "language_preference_modal_reject",
        modal_name: "language_preference_modal",
        suggested_language: suggestedLanguage,
        suggested_language_name: languageName,
        stayed_in_language: "en",
        current_path: currentPath,
      },
    });

    saveLanguagePreference("en");
    dispatch(updateUserLanguage("en"));
    onClose();
  };

  const handleDismiss = (dismissMethod: string) => {
    logAppEvent({
      eventName: APP_EVENTS.MODAL_INTERACTED,
      payload: {
        modal_name: "language_preference_modal",
        action: "dismissed",
        suggested_language: suggestedLanguage,
        suggested_language_name: languageName,
        current_path: currentPath,
        dismiss_method: dismissMethod,
      },
    });
    onClose();
  };

  return (
    <div
      className={styles.overlay}
      onClick={() => handleDismiss("overlay_click")}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleDismiss("escape_key");
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Close modal overlay"
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-preference-title"
      >
        <div className={styles.header}>
          <h2 id="language-preference-title" className={styles.title}>
            Language Preference
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => handleDismiss("close_button")}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <p className={styles.message}>
            We noticed you previously used our site in <strong>{languageName}</strong>.
          </p>
          <p className={styles.question}>Would you like to continue in {languageName}?</p>
        </div>

        <div className={styles.actions}>
          <button className={styles.primaryButton} onClick={handleSwitchLanguage} type="button">
            Yes, switch to {languageName}
          </button>
          <button className={styles.secondaryButton} onClick={handleStayInEnglish} type="button">
            No, stay in English
          </button>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(LanguagePreferenceModal, {
  componentName: "LanguagePreferenceModal",
});
